import { useEffect, useState } from 'react'
import { api, type FeedSettings, type FeedStatus, type FeedTest } from '../api'

/**
 * Automatic loading — TECHNICAL_DESIGN.md §7.2, IMPLEMENTATION.md decisions 43 and 66.
 *
 * Where holdings files are collected from: a custodian's or administrator's SFTP
 * server, or one offering FTP over TLS (IMPLEMENTATION.md decision 45). What each pass collected, file by file, is in Scheduled Jobs, beside the
 * snapshots it loaded. Benchmark figures files come from the same server, from a folder of
 * their own where one is named (decision 66).
 *
 * **The server's key is trusted by a person.** Testing the connection shows the key
 * the server presented; an administrator compares it with the one their provider gave
 * them and trusts it. Nothing is read before, and a server presenting any other key
 * afterwards is refused. Over FTPS a certificate valid for the host name needs nothing
 * trusted; one issued for another name, as a shared host's is, is trusted the same way.
 *
 * **On is its own act**, taking effect at once, as on Guidelines Mailbox Setup: Save keeps the
 * stored on or off.
 *
 * **Secrets are write-only.** The password and the private key are never sent to
 * this screen; a blank field keeps the stored one.
 */

export const OUTCOME: Record<FeedStatus, string> = {
  loaded: 'Loaded',
  duplicate: 'Already loaded',
  refused: 'Refused',
  awaiting_mapping: 'Waiting for a mapping',
  retrying: 'Waiting to be read again',
}

const BLANK = {
  protocol: 'sftp' as FeedSettings['protocol'],
  host: '',
  port: '22',
  username: '',
  sign_in: 'password' as FeedSettings['sign_in'],
  password: '',
  private_key: '',
  folder: '.',
  pattern: '*.csv, *.xlsx',
  move_to: '',
  figures_folder: '',
  figures_pattern: '*.csv, *.xlsx',
}

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function kb(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

type Listed = FeedTest['files']

function FileList({ files, folder, testId }: { files: Listed; folder: string; testId: string }) {
  if (files.length === 0) return <p className="empty">No file in {folder} matches.</p>
  return (
    <div className="table-scroll">
      <table className="grid" data-testid={testId}>
        <thead>
          <tr>
            <th>File</th>
            <th className="num">Size</th>
            <th>Modified</th>
            <th>Collected</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.name}>
              <td>{f.name}</td>
              <td className="num">{kb(f.size)}</td>
              <td>{new Date(f.modified).toLocaleString()}</td>
              <td>{f.handled ? OUTCOME[f.handled] : 'Not yet'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function HoldingsFeed() {
  const [settings, setSettings] = useState<FeedSettings | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState(BLANK)
  const [probe, setProbe] = useState<FeedTest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function fill(found: FeedSettings) {
    setForm({
      protocol: found.protocol,
      host: found.host,
      port: String(found.port),
      username: found.username,
      sign_in: found.sign_in,
      password: '',
      private_key: '',
      folder: found.folder,
      pattern: found.pattern,
      move_to: found.move_to ?? '',
      figures_folder: found.figures_folder ?? '',
      figures_pattern: found.figures_pattern ?? '*.csv, *.xlsx',
    })
  }

  useEffect(() => {
    void (async () => {
      try {
        const found = await api.feedSettings()
        setSettings(found)
        if (found) fill(found)
      } catch (e) {
        setError(said(e))
      } finally {
        setLoaded(true)
      }
    })()
  }, [])

  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await work()
    } catch (e) {
      setError(said(e))
    } finally {
      setBusy(false)
    }
  }

  const save = () =>
    act(async () => {
      const signIn = form.protocol === 'ftps' ? 'password' : form.sign_in
      const saved = await api.saveFeedSettings({
        protocol: form.protocol,
        host: form.host,
        port: Number(form.port) || null,
        username: form.username,
        sign_in: signIn,
        // Blank keeps the stored one. The screen never received it.
        password: signIn === 'password' ? form.password || null : null,
        private_key: signIn === 'key' ? form.private_key || null : null,
        folder: form.folder,
        pattern: form.pattern,
        move_to: form.move_to || null,
        figures_folder: form.figures_folder || null,
        figures_pattern: form.figures_pattern,
        enabled: settings?.enabled ?? false,
      })
      const forgotten = Boolean(settings?.host_key) && !saved.host_key
      setSettings(saved)
      fill(saved)
      setProbe(null)
      setNotice(
        forgotten
          ? 'Saved. This is another server, so its key must be trusted before files are collected again.'
          : 'Saved.',
      )
    })

  /** On or off at once, with the stored settings sent back as they are. */
  const turn = (on: boolean) =>
    act(async () => {
      if (!settings) return
      setSettings(
        await api.saveFeedSettings({
          protocol: settings.protocol,
          host: settings.host,
          port: settings.port,
          username: settings.username,
          sign_in: settings.sign_in,
          password: null,
          private_key: null,
          folder: settings.folder,
          pattern: settings.pattern,
          move_to: settings.move_to,
          figures_folder: settings.figures_folder,
          figures_pattern: settings.figures_pattern,
          enabled: on,
        }),
      )
    })

  const test = () =>
    act(async () => {
      setProbe(null)
      setProbe(await api.testFeed())
    })

  const trust = (key: string) =>
    act(async () => {
      setSettings(await api.trustFeedKey(key))
      setProbe((p) => (p ? { ...p, trusted_key: key } : p))
      setNotice(`Trusted. Files are collected only from a server presenting this ${thing}.`)
    })

  if (!loaded) return <p className="loading">Loading…</p>

  // What an administrator trusts: the server's key over SFTP, its certificate over FTPS.
  const thing = (probe?.protocol ?? settings?.protocol) === 'ftps' ? 'certificate' : 'key'

  return (
    <section className="holdings-feed">
      <header className="page-head">
        <h2>SFTP - Auto loading snapshots</h2>
        <p className="note">
          Holdings files collected from your custodian’s or administrator’s SFTP or FTPS server every 15
          minutes, loaded with the mapping saved for their layout, and evaluated as they arrive. Benchmark figures
          files come from a folder of their own on the same server. Scheduled Jobs shows each pass, file by file.
        </p>
      </header>

      {settings && (
        <p className={settings.state ? 'warning' : 'ok'} data-testid="feed-state">
          {settings.state ? `${settings.state}. ` : 'On: new files are collected every 15 minutes. '}
          {(settings.host_key || settings.protocol === 'ftps') && (
            <button className="ghost" disabled={busy} onClick={() => void turn(!settings.enabled)}>
              {settings.enabled ? 'Turn off' : 'Turn on'}
            </button>
          )}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="ok">{notice}</p>}

      <div className="card">
        {!settings && <p className="note">Not set up yet. Fill in the server your provider gave you and save.</p>}
        <div className="form">
          <label>
            Connect with
            <select
              value={form.protocol}
              onChange={(e) => {
                const protocol = e.target.value as FeedSettings['protocol']
                // The usual port follows the protocol, unless another was typed in.
                const usual = form.protocol === 'ftps' ? '21' : '22'
                setForm({
                  ...form,
                  protocol,
                  port: form.port === '' || form.port === usual ? (protocol === 'ftps' ? '21' : '22') : form.port,
                })
              }}
            >
              <option value="sftp">SFTP</option>
              <option value="ftps">FTP over TLS (FTPS)</option>
            </select>
          </label>
          <label>
            Host
            <input
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder="sftp.custodian.com"
            />
          </label>
          <label>
            Port
            <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
          </label>
          <label>
            User
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </label>
          {form.protocol === 'sftp' && (
            <label>
              Sign in with
              <select
                value={form.sign_in}
                onChange={(e) => setForm({ ...form, sign_in: e.target.value as FeedSettings['sign_in'] })}
              >
                <option value="password">A password</option>
                <option value="key">A private key</option>
              </select>
            </label>
          )}
          {form.protocol === 'ftps' || form.sign_in === 'password' ? (
            <label>
              Password
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={settings?.sign_in === 'password' ? 'unchanged' : ''}
              />
              <small>Never shown again once saved. Leave it blank to keep the one saved.</small>
            </label>
          ) : (
            <label>
              Private key
              <textarea
                rows={5}
                value={form.private_key}
                onChange={(e) => setForm({ ...form, private_key: e.target.value })}
                placeholder={settings?.sign_in === 'key' ? 'unchanged' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
              />
              <small>Without a passphrase; your provider holds the public half. Never shown again once saved.</small>
            </label>
          )}
          <label>
            Folder
            <input value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} />
          </label>
          <label>
            Files to collect
            <input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} />
            <small>Separated by commas, such as *.csv, *.xlsx.</small>
          </label>
          <label>
            Move loaded files to
            <input
              value={form.move_to}
              onChange={(e) => setForm({ ...form, move_to: e.target.value })}
              placeholder="archive"
            />
            <small>A folder on the server. Left empty, files stay where they are and are read once.</small>
          </label>
          <label>
            Benchmark figures folder
            <input
              value={form.figures_folder}
              onChange={(e) => setForm({ ...form, figures_folder: e.target.value })}
              placeholder="benchmarks"
            />
            <small>
              A folder on the same server for your index provider’s figures, in the columns of the Benchmarks template.
              Left empty, figures are loaded by hand under Benchmarks.
            </small>
          </label>
          {form.figures_folder.trim() && (
            <label>
              Figures files to collect
              <input
                value={form.figures_pattern}
                onChange={(e) => setForm({ ...form, figures_pattern: e.target.value })}
              />
              <small>Separated by commas, such as benchmark_figures*.csv.</small>
            </label>
          )}
        </div>
        <div className="actions">
          <button disabled={busy} onClick={() => void save()}>
            Save
          </button>
          <button className="ghost" disabled={busy || !settings} onClick={() => void test()}>
            Test connection
          </button>
        </div>

        {probe && (
          <div className="probe" data-testid="feed-probe">
            {probe.certificate && (
              <p
                className={probe.certificate.valid_for_host ? 'ok' : 'warning'}
                data-testid="feed-certificate"
              >
                {probe.certificate.valid_for_host
                  ? `The server’s certificate is valid for ${settings?.host}, issued by ${probe.certificate.issuer ?? 'a public authority'} until ${probe.certificate.not_after}. Nothing more needs trusting.`
                  : `The server’s certificate is issued for ${probe.certificate.names.join(', ')}, not for ${settings?.host}. Connect by one of those names, or compare the certificate with your provider’s and trust it.`}
              </p>
            )}
            {probe.presented_key &&
              !probe.certificate?.valid_for_host &&
              (probe.presented_key === probe.trusted_key ? (
                <p className="ok">
                  The server presented its trusted {thing}, <code>{probe.presented_key}</code>.
                </p>
              ) : (
                <>
                  <p className="warning">
                    The server presented the {thing} <code>{probe.presented_key}</code>
                    {probe.trusted_key && (
                      <>
                        , not the trusted <code>{probe.trusted_key}</code>
                      </>
                    )}
                    . Compare it with the {thing} your provider gave you before trusting it.
                  </p>
                  <div className="actions">
                    <button disabled={busy} onClick={() => void trust(probe.presented_key!)}>
                      Trust this {thing}
                    </button>
                  </div>
                </>
              ))}
            {!probe.reached ? (
              <p className="error">{probe.detail}</p>
            ) : (
              <>
                <FileList files={probe.files} folder={probe.folder} testId="feed-probe-files" />
                {probe.figures && (
                  <>
                    <h4>Benchmark figures in {probe.figures.folder}</h4>
                    {probe.figures.detail ? (
                      <p className="error">{probe.figures.detail}</p>
                    ) : (
                      <FileList
                        files={probe.figures.files}
                        folder={probe.figures.folder}
                        testId="feed-probe-figures"
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
